import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AnimalSpeciesTransitionPayload,
  Dictionaries,
  DictMap,
  EventProtocolTypePayload,
} from '@/api/dictionaries/types';
import { vaccinationProtocolsApi } from '@/api/vaccination/protocols/api';
import { vaccinationProtocolsTags } from '@/api/vaccination/protocols/tags';
import { DICTIONARY_API } from '@/shared/dict/api';
import { mapSortToQueryParameter, transformDictionariesToMap } from '@/utils/utils';

import { baseQueryWithReAuth } from '../baseApi';
import { dictionariesTags } from './tags';

export const dictionariesApi = createApi({
  reducerPath: 'dictionariesApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(dictionariesTags),
  endpoints: (builder) => ({
    getDictionaries: builder.query<Dictionaries & DictMap, void>({
      query: () => {
        return {
          url: `${DICTIONARY_API}/map`,
          method: 'GET',
        };
      },
      providesTags: [dictionariesTags.DICTIONARIES],
      keepUnusedDataFor: 3600,
      transformResponse: (response: Dictionaries) => {
        return { ...response, dictMap: transformDictionariesToMap(response) };
      },
    }),
    getDictionary: builder.query({
      query: (payload) => ({
        url: `${DICTIONARY_API}/${payload?.dictionaryName}`,
        method: 'GET',
        params: {
          ...payload?.filters,
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: (_r, _e, arg) => [
        { type: dictionariesTags.DICTIONARY, id: arg?.dictionaryName },
      ],
    }),
    getMedicineRecord: builder.query({
      query: (payload) => ({
        url: `${DICTIONARY_API}/drug/${payload.id}`,
        method: 'GET',
        params: {
          ...payload?.filters,
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [dictionariesTags.MEDICINE_RECORD],
    }),
    persistMedicineDictionary: builder.mutation({
      query: (payload) => ({
        url: `${DICTIONARY_API}`,
        method: 'POST',
        body: {
          ...payload,
          type: 'drug',
        },
      }),
      invalidatesTags: [
        { type: dictionariesTags.DICTIONARY, id: 'drug' },
        dictionariesTags.MEDICINE_RECORD,
        dictionariesTags.DICTIONARIES,
      ],
    }),
    deleteMedicineDictionaryRecord: builder.mutation({
      query: (payload) => ({
        url: `${DICTIONARY_API}/drug/${payload?.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: dictionariesTags.DICTIONARY, id: 'drug' },
        dictionariesTags.MEDICINE_RECORD,
        dictionariesTags.DICTIONARIES,
      ],
    }),
    persistAnimalSpeciesTransition: builder.mutation<
      void,
      AnimalSpeciesTransitionPayload
    >({
      query: (payload) => ({
        url: `${DICTIONARY_API}`,
        method: 'POST',
        body: {
          ...payload,
          type: 'animalSpeciesTransition',
        },
      }),
      invalidatesTags: [
        { type: dictionariesTags.DICTIONARY, id: 'animalSpeciesTransition' },
        dictionariesTags.DICTIONARIES,
      ],
    }),
    deleteAnimalSpeciesTransition: builder.mutation<void, { id: string }>({
      query: (payload) => ({
        url: `${DICTIONARY_API}/animalSpeciesTransition/${payload.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: dictionariesTags.DICTIONARY, id: 'animalSpeciesTransition' },
        dictionariesTags.DICTIONARIES,
      ],
    }),
    persistEventProtocolType: builder.mutation<void, EventProtocolTypePayload>({
      query: (payload) => ({
        url: `${DICTIONARY_API}`,
        method: 'POST',
        body: {
          ...payload,
          type: 'eventProtocolType',
        },
      }),
      invalidatesTags: [
        { type: dictionariesTags.DICTIONARY, id: 'eventProtocolType' },
        dictionariesTags.DICTIONARIES,
      ],
      // Схемы мероприятий хранятся в другом api-срезе, поэтому сбрасываем их кэш вручную
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            vaccinationProtocolsApi.util.invalidateTags([
              vaccinationProtocolsTags.PROTOCOLS,
            ]),
          );
        } catch {
          // ошибка показывается общим обработчиком запросов
        }
      },
    }),
  }),
});

export const {
  useGetDictionariesQuery,
  useGetDictionaryQuery,
  useLazyGetDictionaryQuery,
  useGetMedicineRecordQuery,
  useLazyGetMedicineRecordQuery,
  usePersistMedicineDictionaryMutation,
  useDeleteMedicineDictionaryRecordMutation,
  usePersistAnimalSpeciesTransitionMutation,
  useDeleteAnimalSpeciesTransitionMutation,
  usePersistEventProtocolTypeMutation,
} = dictionariesApi;
